import { axios } from '@/_utils/utils';
import { Spinner } from '@/components/_common';
import { useToast } from '@/components/_common/Toast';
import EditorOption from '@/components/Packaging/common/EditorOption';
import { isTimelineItemAssigned } from '@/components/Packaging/typeGuards';
import { BE2FEAssemblyTemplate } from '@/components/Packaging/utils';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectUserId } from '@/redux/selectors/authSelectors';
import { selectTimelineOverlays, selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import { setActiveTemplate } from '@/redux/slices/packagingSlice';
import { AssemblyTemplate, AssemblyTemplateBEResponse, TimelineItem } from '@/redux/slices/packagingSlice.types';
import { PolarisApiResponse } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Box, Button, darken, Fade, FormControl, FormControlLabel, FormLabel, Grid, lighten, Radio, RadioGroup, Tooltip } from '@mui/material';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Modal from '../../_common/Modal/Modal';
import ModalFooterButtons from '../../_common/Modal/ModalFooterButtons';
import PolarisIcon from '../../_common/PolarisIcon';
import useDeleteTemplateConfirmation from '@/components/Packaging/hooks/useDeleteTemplateConfirmation';
import { applyTemplateToTimeline } from './applyTemplateToTimeline';
import SwapTemplateConfirmationDialog from './SwapTemplateConfirmationDialog';

const filterButtons = {
  generic: 'Generic Templates',
  custom: 'Custom Templates',
  user: 'My Templates'
};

type FilterButtonsKeys = keyof typeof filterButtons;

const filterButtonsKeys = Object.keys(filterButtons) as FilterButtonsKeys[];

const TRANSITION_SPEED = 200;

interface SwapTemplateModalProps {
  open: boolean;
  onClose: () => void;
}

const SwapTemplateModal: React.FC<SwapTemplateModalProps> = ({ open, onClose }) => {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [filter, setFilter] = useState<FilterButtonsKeys>('generic');
  const [templateOptions, setTemplateOptions] = useState<Record<string, AssemblyTemplate[]>>(
    filterButtonsKeys.reduce((acc, type) => ({ ...acc, [type]: [] }), {})
  );
  const [selectedTemplate, setSelectedTemplate] = useState<AssemblyTemplate | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [swapTemplatesLoading, setSwapTemplatesLoading] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);
  const overlays = useAppSelector(selectTimelineOverlays);
  const segments = useAppSelector(selectTimelineSegments);
  const userId = useAppSelector(selectUserId);
  const dispatch = useAppDispatch();
  const { error, success } = useToast();
  const showSpinner = templatesLoading || swapTemplatesLoading;

  useEffect(() => {
    if (open) {
      setTemplatesLoading(true);
      getAssemblyTemplates();
    }
  }, [open]);

  const { deleteTemplateDialog, openDialog: openDeleteTemplateDialog } = useDeleteTemplateConfirmation({
    onDeleted: () => {
      setTemplatesLoading(false);
      getAssemblyTemplates();
      success('Template deleted successfully');
    },
    onDeleteFailed: () => {
      setTemplatesLoading(false);
    }
  });

  const toBeApplied: TimelineItem[] = useMemo(() => {
    if (!selectedTemplate?.data) return [];
    return [...(selectedTemplate.data.segments || []), ...(selectedTemplate.data.overlays || []).flat()];
  }, [selectedTemplate]);

  const toBeRemoved = useMemo(() => {
    if (!selectedTemplate?.data) return [];
    const activeOptions = [...(segments || []), ...(overlays || []).flat()];
    const templateOptions = [...(selectedTemplate.data.segments || []), ...(selectedTemplate.data.overlays || []).flat()];

    return activeOptions.filter((activeOption) => {
      const existsInTemplate = templateOptions.findIndex((templateOption) => templateOption.kind === activeOption.kind);
      if (existsInTemplate !== -1) templateOptions.splice(existsInTemplate, 1);
      return existsInTemplate === -1;
    });
  }, [selectedTemplate, overlays, segments]);

  const onFilterClick = (newType: FilterButtonsKeys) => {
    setSelectedTemplate(null);
    setFilter(newType);
  };

  const handleTemplateTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = templateOptions[filter].find((option) => option.id === parseInt(e.target.value)) ?? null;
    setSelectedTemplate(selected);

    setFadeIn(false);
    setTimeout(() => {
      setFadeIn(true);
    }, TRANSITION_SPEED);
  };

  const getAssemblyTemplates = () => {
    axios
      .post<PolarisApiResponse<AssemblyTemplateBEResponse[]>>(`assembly_templates/search/?page_size=999`)
      .then((res) => {
        const data = res.data.data.map(BE2FEAssemblyTemplate);
        const newTemplateOptions: Record<FilterButtonsKeys, AssemblyTemplate[]> = {
          generic: [],
          custom: [],
          user: []
        };
        data.forEach((template) => {
          newTemplateOptions[template.template_type].push(template);
          if (userId === template.created_by?.id) {
            newTemplateOptions.user.push(template);
          }
        });
        setTemplateOptions(newTemplateOptions);
      })
      .catch((err) => {
        console.error('Error: ', err);
      })
      .finally(() => setTemplatesLoading(false));
  };

  const handleSubmit = () => {
    if (confirmationDialogOpen) {
      setConfirmationDialogOpen(false);
    }

    if (!selectedTemplate) {
      error('Please select template first');
      return;
    }

    setSwapTemplatesLoading(true);

    // This request is required to get all up-to-date sources for a template.
    // Saved templates may store sources in segments and overlays. If files are reingested, the data stored in
    // sources becomes outdated (`inventory_id` and `inventory_proxy_id`).
    axios
      .post<PolarisApiResponse<AssemblyTemplateBEResponse>>(`assembly_templates/${selectedTemplate.id}/update_ids/`)
      .then(({ data }) => {
        const existingTimeline = { segments, overlays };
        const template: AssemblyTemplate = BE2FEAssemblyTemplate(data.data);
        const timelineWithAppliedTemplate = applyTemplateToTimeline(existingTimeline, template.data);
        // TODO: I suspect we should not apply the rest of selectedTemplate props, only the data
        const packaging = { ...selectedTemplate, data: timelineWithAppliedTemplate };
        dispatch(setActiveTemplate(packaging));
        onClose();
      })
      .catch((err) => {
        error('Error swapping template');
        console.error(err);
      })
      .finally(() => setSwapTemplatesLoading(false));
  };

  return (
    <>
      <Modal open={open} title="Swap Template" onClose={onClose} scrollDisabled icon={<StyledSwapIcon icon="swap" height={15} />}>
        {showSpinner && <ModalLoader />}

        <Grid container direction="column">
          <Grid item sx={{ height: 50 }}>
            <HeaderDiv>
              <FilterDiv>
                <HighlightBar activeIndex={filterButtonsKeys.indexOf(filter)} />
                {filterButtonsKeys.map((type) => (
                  <FilterButton
                    disableRipple
                    key={`button-filter-${type}`}
                    variant="text"
                    color="primary"
                    onClick={() => onFilterClick(type)}
                    active={+(filter === type)}
                  >
                    {filterButtons[type]}
                  </FilterButton>
                ))}
              </FilterDiv>
            </HeaderDiv>
          </Grid>
          <Grid item>
            <Grid container direction={'row'} spacing={1}>
              <Grid item xs={6}>
                <StyledFormLabel focused={false} indent={+true}>
                  Template Type
                </StyledFormLabel>
                <div style={{ height: 300, overflowY: 'auto' }}>
                  {templateOptions[filter].length === 0 ? (
                    <NoDataMessage>
                      <span>{`No ${filter} templates available`}</span>
                    </NoDataMessage>
                  ) : (
                    <FormControl sx={{ width: '100%' }}>
                      <RadioGroup
                        style={{ overflowY: 'auto' }}
                        value={selectedTemplate?.id || null}
                        onChange={handleTemplateTypeChange}
                        name="template-type-options"
                      >
                        {templateOptions[filter].map((option) => {
                          return (
                            <ControlLabelWrapper key={option.id}>
                              <StyledFormControlLabel
                                value={option.id}
                                control={<StyledRadio color="secondary" />}
                                label={option.name}
                                selected={+(selectedTemplate?.id === option.id)}
                              />
                              <Tooltip title="Delete Template" placement="right">
                                <DeleteIcon
                                  icon="delete"
                                  height={23}
                                  width={23}
                                  onClick={() => {
                                    openDeleteTemplateDialog(option.id);
                                  }}
                                />
                              </Tooltip>
                            </ControlLabelWrapper>
                          );
                        })}
                      </RadioGroup>
                      {deleteTemplateDialog}
                    </FormControl>
                  )}
                </div>
              </Grid>
              <Grid item xs={6}>
                <Grid container direction={'column'}>
                  <Grid item>
                    <StyledFormLabel focused={false}>Template Details</StyledFormLabel>
                  </Grid>
                  <DetailsGrid item style={{ height: 300, overflowY: 'auto' }}>
                    {selectedTemplate === null ? (
                      <NoDataMessage>
                        <span>Select a template to view details</span>
                      </NoDataMessage>
                    ) : (
                      <>
                        {toBeRemoved.map(({ kind, label }, index) => (
                          <Fade key={index} in={fadeIn} timeout={fadeIn ? index * TRANSITION_SPEED : TRANSITION_SPEED}>
                            <div>
                              <StyledEditorOption kind={kind} label={label} toBeRemoved />
                            </div>
                          </Fade>
                        ))}
                        {toBeApplied.map((option, index) => (
                          <Fade key={index} in={fadeIn} timeout={fadeIn ? (toBeRemoved.length + index) * TRANSITION_SPEED : TRANSITION_SPEED}>
                            <div>
                              <StyledEditorOption
                                kind={option.kind}
                                label={option.label}
                                assignedTitleName={isTimelineItemAssigned(option) ? option.source.titleName : undefined}
                              />
                            </div>
                          </Fade>
                        ))}
                      </>
                    )}
                  </DetailsGrid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item sx={{ marginTop: 2 }}>
            <ModalFooterButtons
              submit={{
                disabled: !selectedTemplate?.id,
                onClick: () => {
                  if (toBeRemoved.length > 0) {
                    setConfirmationDialogOpen(true);
                  } else {
                    handleSubmit();
                  }
                }
              }}
              cancel={onClose}
            />
          </Grid>
        </Grid>
      </Modal>

      <SwapTemplateConfirmationDialog
        open={confirmationDialogOpen}
        itemsToBeRemoved={toBeRemoved}
        onSubmit={handleSubmit}
        onCancel={() => setConfirmationDialogOpen(false)}
      />
    </>
  );
};

export default SwapTemplateModal;

const StyledSwapIcon = styled(PolarisIcon)`
  transform: rotate(90deg);
`;

const FilterButton = styled(Button)<{ active: number }>`
  width: 150px;
  color: ${({ theme, active }) => (active ? theme.palette.getContrastText(theme.palette.primary.main) : theme.palette.text.secondary)};
  z-index: 2;
  transition: color 0.3s ease;
  height: 100%;
  white-space: nowrap;

  &:hover {
    background: transparent;
    color: ${({ theme, active }) => (active ? theme.palette.getContrastText(theme.palette.primary.main) : theme.palette.primary.main)};
  }
`;

const HighlightBar = styled.div<{ activeIndex: number }>`
  position: absolute;
  top: 0;
  left: ${({ activeIndex }) => activeIndex * 150 + 'px'};
  width: 150px;
  height: 100%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  transition: left 0.3s ease;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  z-index: 1;
`;

const StyledFormControlLabel = styled(FormControlLabel)<{ selected: number }>`
  background-color: ${({ theme, selected }) =>
    selected ? darken(theme.palette.background.paper, 0.5) : lighten(theme.palette.background.paper, 0.05)};
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.palette.background.default};
  height: 50px;
  padding: ${({ theme }) => theme.spacing(0, 3)};
  margin: 0px;
  padding: 0px;

  .MuiFormControlLabel-label {
    padding-left: ${({ theme }) => theme.spacing(2)};
  }
`;

const StyledFormLabel = styled(FormLabel)<{ indent?: number }>`
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.background.paper)};
  display: flex;
  align-items: center;
  opacity: 0.5;
  padding: ${({ theme, indent }) => theme.spacing(2, indent ? 6.5 : 1)};
`;

const StyledRadio = styled(Radio)`
  height: 100%;
  border-radius: 0px;
  border-right: 1px solid ${({ theme }) => theme.palette.background.default};
`;

const HeaderDiv = styled.div`
  background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.05)};
  padding: ${({ theme }) => theme.spacing(1)};
`;

const DetailsGrid = styled(Grid)`
  background-color: ${({ theme }) => darken(theme.palette.background.default, 0.05)};
`;

const FilterDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
`;

const StyledEditorOption = styled(EditorOption)`
  > div {
    height: 50px;
  }
`;

const NoDataMessage = styled.div`
  opacity: 0.7;
  font-size: 1.4rem;
  font-style: italic;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    width: 200px;
    text-align: center;
  }
`;

const DeleteIcon = styled(PolarisIcon)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  border: 1px solid ${({ theme }) => theme.palette.error.main};
  color: ${({ theme }) => theme.palette.error.main};
  border-radius: 50%;
  padding: 4px;
  transition: opacity 0.3s ease;
  cursor: pointer;
`;

const ControlLabelWrapper = styled.div`
  position: relative;

  &:hover svg {
    opacity: 0.6;
  }

  svg:hover {
    opacity: 1;
  }
`;

const ModalLoader = () => {
  return (
    <>
      <Box
        sx={(theme) => ({
          position: 'absolute',
          zIndex: 5,
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: `${theme.palette.background.paper}80`,
          borderRadius: `${theme.shape.borderRadius}px`
        })}
      />
      <Spinner />
    </>
  );
};
